import {
  BUCKET_FILTER,
  MULTISELECT_FILTER,
  LINK_FILTER,
  ProductColor,
  ProductLister,
  ProductSearchObject,
  RANGE_FILTER,
  SetFilterObject,
  Filter,
  Banner,
} from '@as-react/common-types';
import { isNullOrUndefined } from './general.util';

const isProductColorAvailable = (color: ProductColor): boolean => {
  return color.availability !== 0;
};

const isProductColorDisplayable = (colour: ProductColor) => {
  return isProductColorAvailable(colour) || colour.alwaysVisible;
};

export const getDefaultProductColor = (product: ProductLister) => {
  return product.colours.find(colour => colour.colourId === product.colourId);
};

const isDefaultProductColorAvailable = (product: ProductLister): boolean => {
  const defaultColour = getDefaultProductColor(product);

  if (isNullOrUndefined(defaultColour)) {
    return false;
  }

  return isProductColorDisplayable(defaultColour);
};

export const isMultiSelectFilter = (filterObject: SetFilterObject): boolean =>
  filterObject.filterType === MULTISELECT_FILTER;

export const isRangeFilter = (filterObject: SetFilterObject): boolean => filterObject.filterType === RANGE_FILTER;

export const isBucketFilter = (filterObject: SetFilterObject): boolean => filterObject.filterType === BUCKET_FILTER;

export const isLinkFilter = (filterObject: SetFilterObject): boolean => filterObject.filterType === LINK_FILTER;

const isNotTheDefaultProductColor = (defaultColour: ProductColor) => {
  return (colour: ProductColor) => colour.colourId !== defaultColour.colourId;
};

const removeInvalidProductColours = (product: ProductLister): ProductLister => {
  const defaultColour = getDefaultProductColor(product) as ProductColor;
  const filteredColours = product.colours
    .filter(isNotTheDefaultProductColor(defaultColour))
    .filter((colour: ProductColor) => isProductColorDisplayable(colour));

  return {
    ...product,
    colours: [defaultColour, ...filteredColours],
  };
};

export const filterProductData = (searchResult: ProductSearchObject): ProductSearchObject => {
  const filteredSearchItems: Array<ProductLister> = searchResult
    .items!.filter(product => isDefaultProductColorAvailable(product as ProductLister))
    .map(product => removeInvalidProductColours(product as ProductLister));

  return {
    ...searchResult,
    items: filteredSearchItems,
  };
};

/*
 * Check if default filter is set to Brand
 */
const isDefaultFilterBrand = (filterName: string): boolean => {
  if (isNullOrUndefined(filterName)) {
    return false;
  }

  return filterName.includes('brand');
};

/*
 * Check if default filter is set to Brand
 */
const removeBrandFilterIfAny = (productSearchObject: ProductSearchObject): Array<Filter> => {
  return productSearchObject.filters!.filter((item: Filter) => {
    return item.filterOn !== 'brand';
  });
};

/*
 * Check if default filter is set to Brand
 */
export const removeBrandFilterIfBrandPage = (
  filterName: string,
  productSearchObject: ProductSearchObject
): Array<Filter> => {
  if (isDefaultFilterBrand(filterName)) {
    return removeBrandFilterIfAny(productSearchObject);
  }
  return productSearchObject.filters!;
};

export const mergeBannersInProductArray = (searchResult: ProductSearchObject): Array<ProductLister | Banner> => {
  let productBanners: Array<ProductLister | Banner> = [];
  if (!isNullOrUndefined(searchResult.items)) {
    productBanners = [...searchResult.items!];
  }

  if (!isNullOrUndefined(searchResult.banners)) {
    searchResult.banners?.reverse().forEach((banner: Banner) => {
      if (!isNullOrUndefined(banner.listerPosition) && productBanners.length >= banner.listerPosition) {
        const positionIndex = banner.listerPosition - 1;
        productBanners = [...productBanners.slice(0, positionIndex), banner, ...productBanners.slice(positionIndex)];
      }
    });
  }

  return productBanners;
};

export const handleProductSearchObject = (searchResult: ProductSearchObject, defaultFilter: string) => {
  const filteredProductSearchObject = filterProductData(searchResult);
  return {
    ...filteredProductSearchObject,
    items: mergeBannersInProductArray(filteredProductSearchObject),
    filters: removeBrandFilterIfBrandPage(defaultFilter, filteredProductSearchObject),
  };
};
